import { useState } from "react";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";
import { addYears } from "date-fns";

import { Heading, Paragraph } from "common/core/typography";
import Button from "common/core/button";
import { useForm } from "common/core/form";
import { AutomaticFormRow } from "common/core/form/layout";
import { TextInput } from "common/core/form/text";
import { isAriaInvalid, defaultRequiredMessage } from "common/core/form/error";
import { FormattedDate } from "common/core/format/date";
import { userFullName } from "util/user";
import { b } from "util/html";
import { useCompanyName } from "util/feature_detection";
import CertificateCard from "common/notary/certificate/card";

import Styles from "./index.module.scss";
import type { NotaryProfileWizardProofCertificate as User } from "../index_fragment.graphql";

type FormValues = {
  pin: string;
  pinVerify: string;
};
type Props = {
  user: User;
  onGenerateCertificate: (pin: string) => Promise<void>;
};

const MESSAGES = defineMessages({
  confirmError: {
    id: "8b14519c-16ca-45ec-a5fd-eb5c6c8a2650",
    defaultMessage: "Pins do not match",
  },
});

function CertificateDisplay(props: { user: User; issueDate: Date }) {
  const { user } = props;
  const notaryProfile = user.notaryProfile!;
  return (
    <div className={Styles.certCardContainer}>
      <CertificateCard user={user} />
      <dl>
        <FormattedMessage
          id="4f169530-7cb9-449f-a01c-1047debbc38a"
          defaultMessage="Full name"
          tagName="dt"
        />
        <dd>{userFullName(user)}</dd>

        <FormattedMessage
          id="d56bc89b-e4cc-4019-b645-9300ea6b73d0"
          defaultMessage="Commission number"
          tagName="dt"
        />
        <dd>{notaryProfile.notaryId}</dd>

        <FormattedMessage
          id="9f18f2ae-055e-4b79-8270-35aa0675ed4b"
          defaultMessage="Date of issuance"
          tagName="dt"
        />
        <dd>
          <FormattedDate value={props.issueDate} />
        </dd>
      </dl>
    </div>
  );
}

function NotaryCertificateCreation({ user, onGenerateCertificate }: Props) {
  const issueDate = new Date();
  const expiryDate = addYears(issueDate, 1);

  const intl = useIntl();
  const companyName = useCompanyName();
  const form = useForm<FormValues>();

  const [isLoading, setIsLoading] = useState(false);

  const handleGenerate = (values: FormValues) => {
    setIsLoading(true);
    onGenerateCertificate(values.pin).catch(() => {
      setIsLoading(false);
    });
  };
  return (
    <form onSubmit={form.handleSubmit(handleGenerate)} className={Styles.main}>
      <Heading textStyle="headingTwo" level="h1">
        <FormattedMessage
          id="44d77a69-80cb-4576-86ee-2de9684bd936"
          defaultMessage="Proof Digital Certificate"
        />
      </Heading>
      <Paragraph>
        <FormattedMessage
          id="13c1a518-de7f-4dca-9d43-f60671a90c44"
          defaultMessage="To take calls on {companyName}, you’ll need to create your Proof digital certificate in order to take calls on our platform"
          values={{ companyName }}
        />
      </Paragraph>
      <Paragraph className={Styles.break}>
        <FormattedMessage
          id="5f3d73cd-eb85-4a17-b796-03767a11e2a5"
          defaultMessage="Your certificate will be associated with this identity:"
          tagName="strong"
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          id="553dbd47-cffe-4152-823b-611ffa5fd444"
          defaultMessage="Double check your profile details are correct. You may need to generate a new certificate if issued incorrectly"
        />
      </Paragraph>
      <CertificateDisplay user={user} issueDate={issueDate} />

      <Heading textStyle="headingFive" level="h2">
        <FormattedMessage
          id="e654be8a-4a6f-4df0-95b1-8068da43fe73"
          defaultMessage="Create backup verification pin"
        />
      </Heading>
      <Paragraph>
        <FormattedMessage
          id="97d3226d-ca1a-4237-b7ce-35bfcd3232ac"
          defaultMessage="Create your 4-digit personal pin number. This number is a backup verification method to secure your account, should you lose access to your authenticator device. Make sure you don't misplace or forget your personal pin."
        />
      </Paragraph>
      <AutomaticFormRow
        className={Styles.break}
        fullWidth
        required
        label={
          <FormattedMessage
            id="86ff3e50-ff9b-451d-8d06-f13ced43b815"
            defaultMessage="Enter 4-digit pin number"
          />
        }
        helperText={
          <FormattedMessage
            id="d278422f-9852-477f-80ac-00e3712963b3"
            defaultMessage="Choose a pin that is easy to remember"
          />
        }
        form={form}
        name="pin"
        registerOptions={{
          required: defaultRequiredMessage(intl),
          maxLength: 4,
          pattern: /^\d{4}$/,
        }}
      >
        <TextInput
          type="password"
          inputMode="decimal"
          maxLength={4}
          aria-invalid={isAriaInvalid(form.formState.errors.pin)}
        />
      </AutomaticFormRow>
      <AutomaticFormRow
        fullWidth
        required
        label={
          <FormattedMessage
            id="4b1948f2-ce82-4da8-9cc7-1bad08ef21c1"
            defaultMessage="Confirm 4-digit pin number"
          />
        }
        form={form}
        name="pinVerify"
        registerOptions={{
          required: defaultRequiredMessage(intl),
          maxLength: 4,
          validate: (value, formValues) => {
            return value === formValues.pin || intl.formatMessage(MESSAGES.confirmError);
          },
        }}
      >
        <TextInput
          type="password"
          inputMode="decimal"
          maxLength={4}
          aria-invalid={isAriaInvalid(form.formState.errors.pinVerify)}
        />
      </AutomaticFormRow>

      <Heading textStyle="headingFive" level="h2">
        <FormattedMessage
          id="eabcef40-e30c-4579-babc-8f78fc34138c"
          defaultMessage="Payment method"
        />
      </Heading>
      <Paragraph>
        <FormattedMessage
          id="74836204-7d1a-45b3-a366-1ed15d8a953f"
          defaultMessage="Your 1st year of a Proof Digital Certificate will be free of cost. Upon renewal your credit card on file will be used. This can be edited in settings."
        />
      </Paragraph>

      <h3 className={Styles.paymentHeader}>
        <FormattedMessage
          id="62787e4c-f74c-4e20-8e40-6bd32699f97f"
          defaultMessage="Cost of certificate"
        />
      </h3>
      <Paragraph>
        <FormattedMessage
          id="249bac90-5350-4942-a556-83f5e13d356a"
          defaultMessage="<s>$50</s> <b>$0</b> 1 year -- expires {expiryDate}"
          values={{
            b,
            s: (inner) => <s>{inner}</s>,
            expiryDate: <FormattedDate value={expiryDate} />,
          }}
        />
      </Paragraph>

      <div className={Styles.footer}>
        <Button
          type="submit"
          buttonColor="action"
          variant="primary"
          disabled={!form.formState.isValid}
          isLoading={isLoading}
        >
          <FormattedMessage
            id="ae872354-87d2-43c7-a7ad-e1b97475a59a"
            defaultMessage="Generate certificate"
          />
        </Button>
      </div>
    </form>
  );
}

export default NotaryCertificateCreation;
